/** Hostname from Website Designer domain field (protocol/path/port stripped; env labels preserved). */
export function shopCustomDomainHost(input: string | null | undefined): string {
  let host = String(input || '').trim().toLowerCase()
  if (!host) return ''
  return host.replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/:\d+$/, '')
}

/**
 * Equality key: strip a single leading www. only.
 * `www.storesch.com` ≡ `storesch.com`, but `dev.storesch.com` stays distinct.
 */
export function normalizeShopDomainLike(input: string | null | undefined): string {
  const host = shopCustomDomainHost(input)
  return host.startsWith('www.') ? host.slice(4) : host
}

/**
 * Public customer-facing storefront URL.
 * Prefers custom domain from Website Designer when set; otherwise /shop/{slug} on the platform origin.
 * Uses the stored host as-is (including env subdomains like dev./uat.).
 */
export function buildShopPublicStorefrontUrl(opts: {
  slug: string
  domain?: string | null
  /** Path under the storefront root (e.g. "", "/products", "/about"). "home" → root. */
  path?: string | null
  platformOrigin?: string
}): string {
  const slug = String(opts.slug || '').trim()
  const domain = shopCustomDomainHost(opts.domain)
  let path = String(opts.path || '').trim()
  if (path === 'home' || path === '/home') path = ''
  if (path && !path.startsWith('/')) path = `/${path}`

  if (domain) {
    return `https://${domain}${path}`
  }

  const origin = String(
    opts.platformOrigin ||
      (typeof window !== 'undefined' ? window.location.origin : ''),
  ).replace(/\/$/, '')
  if (!slug) return origin || '/'
  const base = `${origin}/shop/${encodeURIComponent(slug)}`
  return `${base}${path}`
}

/**
 * When browsing /shop/{slug} on a platform host, redirect to the mapped custom domain
 * with the equivalent path. Returns null when no redirect should happen.
 */
export function customDomainRedirectTarget(opts: {
  domain?: string | null
  /** Storefront base path, e.g. `/shop/cadenzaharps`. Empty on custom-domain mode. */
  basePath: string
  pathname: string
  search?: string
  hash?: string
  hostname?: string
}): string | null {
  const domain = shopCustomDomainHost(opts.domain)
  if (!domain) return null

  const basePath = String(opts.basePath || '').replace(/\/$/, '')
  if (!basePath.startsWith('/shop/')) return null

  const hostname = String(
    opts.hostname || (typeof window !== 'undefined' ? window.location.hostname : ''),
  ).toLowerCase()
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') return null

  // www ↔ apex only; never treat dev./uat. as the same as apex
  if (normalizeShopDomainLike(domain) === normalizeShopDomainLike(hostname)) return null

  let rest = opts.pathname || '/'
  if (rest === basePath) rest = '/'
  else if (rest.startsWith(`${basePath}/`)) rest = rest.slice(basePath.length) || '/'
  else return null

  const path = rest === '/' ? '' : rest
  return `https://${domain}${path}${opts.search || ''}${opts.hash || ''}`
}
